import { createContext, useContext, type ReactNode } from 'react';
import type { PublicUser } from '@/lib/api';
import { useCurrentUser } from './hooks';

/**
 * App-wide auth state. Resolves the session ONCE (via the shared /me query) and
 * exposes it to guards, the layout, and any screen — so nothing re-fetches
 * identity and everything agrees on who is signed in.
 */
interface AuthState {
  user: PublicUser | null;
  /** Still resolving the initial session (show a splash, not a redirect). */
  isLoading: boolean;
  /** A real failure resolving the session (network/500), distinct from "logged out". */
  isError: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useCurrentUser();
  const value: AuthState = {
    user: data ?? null,
    isLoading,
    isError,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
